import { Formik, FormikHelpers, FormikProps } from "formik";
import _, { cloneDeep } from "lodash";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  EventReportInfo,
  EventReportWithDataFilesInfo,
  SubmitEventReportWithDataFiles,
} from "../../../generated/data-contracts";
import {
  AvisUtils,
  ErrorUtils,
  IAxiosError,
  LicenseesUrls,
  LicenseesUtils,
  api,
  prepareCollectionForPost,
  nullsToUndefined,
} from "../../../utils";
import { AvisLoader } from "../../shared/components";
import {
  NotPortalReportMessage,
  SaveAndSubmit,
} from "../../shared/components/form";
import { AvisConfirmModalShort } from "../../shared/components/modal";
import { AvisContext } from "../../shared/context/AvisContext";
import { LicenseReportForm } from "./components/LicenseReportForm";
import { LicenseReportHeader } from "./components/LicenseReportHeader";
import { ReportWithDataFilesForm } from "./components/ReportForms";
import { validationWithDataFile } from "./validation/validation";

export function LicenseReportPageWithDataFiles() {
  const { res, ctxPortalSettings } = useContext(AvisContext);
  const [error, setError] = useState<IAxiosError>();
  const [firstLoading, setFirstLoading] = useState<boolean>(true);
  const [loadingBriefInfo, setLoadingBriefInfo] = useState<boolean>(true);
  const [licenseReport, setLicenseReport] = useState<
    EventReportWithDataFilesInfo | undefined
  >();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const loadReportData = useCallback(
    (formikHelper?: FormikHelpers<SubmitEventReportWithDataFiles>) => {
      if (isSubmitting) return;
      const urlParams = LicenseesUtils.getUrlParams();
      if (!urlParams.licenseId || !urlParams.licenseInvoicePlanId) return;

      setLoadingBriefInfo(true);
      api
        .licenseesGetEventReportWithDataFiles(
          urlParams.licenseId,
          urlParams.licenseInvoicePlanId,
        )
        .then((res) => {
          const dataNormalized = nullsToUndefined<EventReportWithDataFilesInfo>(
            res.data,
          );
          setLicenseReport(dataNormalized);
          if (formikHelper) {
            const submitDataObject = { ...dataNormalized, submitReport: false };
            formikHelper.resetForm({
              values: submitDataObject as SubmitEventReportWithDataFiles,
            });
          }
        })
        .catch(setError)
        .finally(() => {
          setLoadingBriefInfo(false);
          setFirstLoading(false);
        });
    },
    [isSubmitting],
  );

  useEffect(() => {
    if (firstLoading) loadReportData();
  }, [firstLoading, loadReportData]);

  const hasNoPortalReport = useMemo(
    () =>
      LicenseesUtils.hasNoPortalReport(
        licenseReport?.portalReportStatusType,
        licenseReport?.eventNotOccuredPortal,
        licenseReport?.rows?.length,
      ),
    [licenseReport],
  );

  const handleSubmit = (
    formikValues: SubmitEventReportWithDataFiles,
    formikHelpers: FormikHelpers<SubmitEventReportWithDataFiles>,
  ) => {
    if (!licenseReport) return;

    setIsSubmitting(true);

    const values = _.cloneDeep(formikValues);
    if (values.eventNotOccuredPortal) {
      //Empty bag rows with possible incomplete data
      values.rows = [];
    } else {
      prepareCollectionForPost(values, "rows");
    }

    api
      .licenseesPostEventReportWithDataFiles(
        licenseReport.licenseHeaderId,
        licenseReport.licenseInvoicePlanId,
        values,
      )
      .then(() => {
        ErrorUtils.showSuccessfulMessage(
          res("message_Success"),
          values.submitReport
            ? res("licensees_LicenseReports_notification_Submit")
            : res("licensees_LicenseReports_notification_Save"),
        );

        if (values.eventNotOccuredPortal) {
          //Manually clear bag rows
          values.rows = [];
        }
        if (values.submitReport) setSubmitted(true);
        loadReportData(formikHelpers);
      })
      .catch((err) => ErrorUtils.handleFormValidation(err, formikHelpers))
      .finally(() => {
        setIsSubmitting(false);
        setIsModalOpen(false);
      });
  };

  const renderActionButtons = useCallback(
    (
      editAllowed: boolean,
      formikProps: FormikProps<SubmitEventReportWithDataFiles>,
    ) => {
      if (!editAllowed || submitted) return;

      return (
        <SaveAndSubmit
          formikProps={formikProps}
          disabled={isSubmitting}
          setIsModalOpen={setIsModalOpen}
        />
      );
    },
    [isSubmitting, submitted],
  );

  const renderForm = useCallback(
    (formikProps: FormikProps<SubmitEventReportWithDataFiles>) => {
      const editAllowed = AvisUtils.isEditablePortalReportStatusTypeInfo(
        licenseReport?.portalReportStatusType,
      );
      if (loadingBriefInfo && firstLoading) return <AvisLoader inline />;

      if (hasNoPortalReport) return <NotPortalReportMessage />;

      return (
        <>
          <AvisConfirmModalShort
            isOpen={isModalOpen}
            content={res("licensees_LicenseReports_notification_ConfirmSubmit")}
            confirm={formikProps.submitForm}
            cancel={() => setIsModalOpen(false)}
            title={res(
              "licensees_LicenseReports_notification_ConfirmSubmitTitle",
            )}
            disableControls={isSubmitting}
          />
          <ReportWithDataFilesForm
            bag={formikProps}
            bagField="rows"
            licenseReport={licenseReport}
            disabled={!editAllowed || submitted}
            loading={isSubmitting}
            allowBlankReport={
              licenseReport?.licenseSettingsInfo?.allowBlankReport
            }
            allowedFileFormats={ctxPortalSettings?.uploadableDocumentTypes}
          />
          {renderActionButtons(editAllowed, formikProps)}
        </>
      );
    },
    [
      licenseReport,
      loadingBriefInfo,
      firstLoading,
      hasNoPortalReport,
      isModalOpen,
      res,
      isSubmitting,
      submitted,
      ctxPortalSettings?.uploadableDocumentTypes,
      renderActionButtons,
    ],
  );

  if (!licenseReport) return <AvisLoader />;

  return (
    <LicenseReportForm
      data={cloneDeep(licenseReport) as EventReportInfo}
      error={error}
      loading={loadingBriefInfo && firstLoading}
      backLink={{
        title: res("label_Back"),
        url: LicenseesUtils.getUrlWithParams(
          LicenseesUrls.licenseeReports,
          {},
          true,
        ),
      }}
    >
      <LicenseReportHeader data={licenseReport} />

      <Formik<SubmitEventReportWithDataFiles>
        initialValues={{
          eventNotOccuredPortal: licenseReport?.eventNotOccuredPortal,
          submitReport: false,
          rows: licenseReport?.rows ?? [],
        }}
        onSubmit={handleSubmit}
        validationSchema={validationWithDataFile(
          res,
          ctxPortalSettings?.uploadableDocumentTypes,
          ctxPortalSettings?.unsupportedFilenameSymbols ?? [],
          ctxPortalSettings?.maxFileUploadSize ?? 0,
        )}
        validateOnBlur={false}
        validateOnChange={false}
      >
        {renderForm}
      </Formik>
      {isSubmitting && <AvisLoader />}
    </LicenseReportForm>
  );
}
